import React, { ChangeEvent, useState } from "react";
import { PurchaseModal } from "../purchaseModal/purchase-modal.component";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import {
  purchaseSessionPointsStyles,
  tabsStyles,
  tabItemStyles,
} from "./purchase-session-points.styles";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { capitalizeCamelCase } from "../../utils/capitalize-camel-case";

export interface MembershipProduct {
  id: number;
  name: string;
  price: number;
  category?: string;
}

export interface MembershipProductCategories {
  billingCategory: string;
  products: Array<MembershipProduct>;
}

export interface DialogPurchaseSessionPointsProps {
  products: Array<MembershipProductCategories>;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (product: MembershipProduct) => void;
}

export const DialogPurchaseSessionPoints = ({
  products,
  isOpen,
  onClose,
  onAddToCart,
}: DialogPurchaseSessionPointsProps) => {
  const classes = purchaseSessionPointsStyles();
  const tabsClasses = tabsStyles();
  const tabItemClasses = tabItemStyles();

  const hasProducts = products?.length > 0;
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(0);

  const selectedCategory = hasProducts
    ? products[selectedCategoryIndex].billingCategory
    : null;
  const productGroup = selectedCategory
    ? products.find((x) => x.billingCategory == selectedCategory)
    : null;

  const handleCategoryChange = (e: ChangeEvent, newValue: number) => {
    setSelectedCategoryIndex(newValue);
    setSelectedProduct(0);
  };

  const handleProductChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedProduct(parseInt(e.target.value));
  };

  const renderCustomLabelFor = (product: MembershipProduct) => {
    return (
      <span className={classes.productLabel}>
        <div className={classes.priceContainer}>{`$${product.price.toFixed(
          2
        )}`}</div>
        <label dangerouslySetInnerHTML={{ __html: product.name }} />
      </span>
    );
  };

  const handleAddToCartClick = () => {
    if (selectedProduct <= 0) {
      return;
    }
    const productToAdd = productGroup.products.find(
      (p) => p.id == selectedProduct
    );
    onAddToCart(productToAdd);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <PurchaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Unlock the value of mentorship!"
      subtitle="Find clarity, get unstuck, and accelerate your growth with 1-on-1 mentorship."
    >
      {hasProducts && (
        <Grid
          container
          justifyContent="center"
          className={classes.menuContainer}
        >
          <Tabs
            value={selectedCategoryIndex}
            onChange={handleCategoryChange}
            centered
            classes={tabsClasses}
          >
            {products.map((product) => (
              <Tab
                key={`tab-${product.billingCategory}`}
                disableRipple
                label={capitalizeCamelCase(product.billingCategory)}
                classes={tabItemClasses}
              />
            ))}
          </Tabs>
        </Grid>
      )}

      <Card className={classes.plansContainer}>
        <CardContent className={classes.cardContent}>
          {hasProducts && (
            <>
              <Typography className={classes.cardTitle}>
                Billed {selectedCategory.toLowerCase()}
              </Typography>

              <RadioGroup
                aria-label="products"
                name="products"
                value={selectedProduct}
                onChange={handleProductChange}
              >
                {productGroup.products.map((product) => (
                  <FormControlLabel
                    key={product.id}
                    value={product.id}
                    control={<Radio />}
                    label={renderCustomLabelFor(product)}
                  />
                ))}
              </RadioGroup>
            </>
          )}

          {!hasProducts && (
            <Box sx={{ m: 2 }}>
              <Typography variant="subtitle1">
                There are no products available right now.
              </Typography>
            </Box>
          )}

          <hr className={classes.separator} />
          <Grid container justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              className={classes.addToCartButton}
              endIcon={<ChevronRightIcon fontSize="large" />}
              disabled={!hasProducts || selectedProduct === 0}
              onClick={handleAddToCartClick}
            >
              Add to cart
            </Button>
          </Grid>
        </CardContent>
      </Card>
    </PurchaseModal>
  );
};
